/**
 * JSON Cleaner Utility
 * Handles escaped JSON strings from various sources (MCP export, Claude, etc.)
 */

export interface CleanJSONResult {
  success: boolean
  cleaned?: string
  parsed?: any
  error?: string
  wasEscaped?: boolean
}

/**
 * Clean escaped JSON string
 * Handles: \n, \", \\, \t, \r
 */
export function cleanEscapedJSON(input: string): CleanJSONResult {
  try {
    // Trim whitespace
    let cleaned = input.trim()

    // Check if the string is already valid JSON
    try {
      const parsed = JSON.parse(cleaned)
      return {
        success: true,
        cleaned,
        parsed,
        wasEscaped: false,
      }
    } catch {
      // Not valid JSON yet, continue cleaning
    }

    // Detect if this is an escaped JSON string
    const hasEscapedNewlines = cleaned.includes('\\n')
    const hasEscapedQuotes = cleaned.includes('\\"')
    const wasEscaped = hasEscapedNewlines || hasEscapedQuotes

    if (wasEscaped) {
      // Replace escaped characters
      cleaned = cleaned
        .replace(/\\n/g, '\n')      // Newlines
        .replace(/\\t/g, '\t')      // Tabs
        .replace(/\\r/g, '\r')      // Carriage returns
        .replace(/\\"/g, '"')       // Quotes
        .replace(/\\\\/g, '\\')     // Backslashes (do this last!)
    }

    // Try to parse the cleaned JSON
    const parsed = JSON.parse(cleaned)

    return {
      success: true,
      cleaned,
      parsed,
      wasEscaped,
    }
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error),
    }
  }
}

/**
 * Pretty print JSON object
 */
export function prettyPrintJSON(obj: any, indent: number = 2): string {
  return JSON.stringify(obj, null, indent)
}

/**
 * Validate JSON structure for diagram data
 */
export function validateDiagramJSON(obj: any): { valid: boolean; error?: string } {
  if (!obj || typeof obj !== 'object') {
    return { valid: false, error: 'JSON must be an object' }
  }

  // Check for required fields
  if (!obj.nodes || !Array.isArray(obj.nodes)) {
    return { valid: false, error: 'Missing or invalid "nodes" array' }
  }

  if (!obj.edges || !Array.isArray(obj.edges)) {
    return { valid: false, error: 'Missing or invalid "edges" array' }
  }

  // Optional but recommended fields
  if (obj.diagramType && typeof obj.diagramType !== 'string') {
    return { valid: false, error: '"diagramType" must be a string' }
  }

  return { valid: true }
}

/**
 * Auto-clean and validate JSON input
 * This is the main function to use
 */
export function autoCleanAndValidate(input: string): CleanJSONResult {
  console.log('🔧 [jsonCleaner] autoCleanAndValidate called')
  console.log('🔧 [jsonCleaner] Input length:', input.length)

  // Step 1: Clean escaped characters
  const cleanResult = cleanEscapedJSON(input)
  console.log('🔧 [jsonCleaner] Clean result:', cleanResult.success ? '✅ Success' : '❌ Failed')

  if (!cleanResult.success) {
    console.error('❌ [jsonCleaner] Cleaning failed:', cleanResult.error)
    return cleanResult
  }

  // Step 2: Validate diagram structure
  console.log('🔧 [jsonCleaner] Validating diagram structure...')
  const validation = validateDiagramJSON(cleanResult.parsed)
  console.log('🔧 [jsonCleaner] Validation result:', validation)

  if (!validation.valid) {
    console.error('❌ [jsonCleaner] Validation failed:', validation.error)
    return {
      success: false,
      error: `Invalid diagram JSON: ${validation.error}`,
      cleaned: cleanResult.cleaned,
    }
  }

  console.log('✅ [jsonCleaner] All checks passed')

  // Step 3: Pretty print for display
  const prettyJSON = prettyPrintJSON(cleanResult.parsed)

  return {
    success: true,
    cleaned: prettyJSON,
    parsed: cleanResult.parsed,
    wasEscaped: cleanResult.wasEscaped,
  }
}
